/**
 * useTerminal -- Zustand store + hook for the Bloomberg-style command terminal.
 *
 * Manages terminal output history, command history for up/down navigation,
 * command execution via POST /api/terminal/command, and system messages.
 *
 * Supports multiple terminal instances keyed by panel ID for workspace mode.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'
import type { TerminalLine, TerminalResponse } from '@/lib/types'
import { expandAlias, handleAliasCommand } from '@/lib/terminal-aliases'
import { clearPinned, loadPinned, renderPinned, savePinned } from '@/lib/terminal-pinned'

let _lineId = 0
function nextId() {
  return _lineId++
}

function now() {
  return new Date().toLocaleTimeString('en-GB', { hour12: false })
}

// ── Single-instance terminal state (backward compatible) ──────────────────

interface TerminalState {
  /** All output lines displayed in the terminal */
  history: TerminalLine[]
  /** Previous commands for up/down arrow navigation */
  commandHistory: string[]
  /** Whether a command is currently executing */
  executing: boolean

  /** Execute a terminal command -- calls API and appends response to history */
  executeCommand: (cmd: string) => Promise<void>
  /** Clear all output */
  clear: () => void
  /** Add a system message (e.g. from WebSocket agent updates) */
  addSystemMessage: (msg: string) => void
  /** Add an arbitrary line */
  addLine: (line: Omit<TerminalLine, 'id'>) => void
}

export const useTerminalStore = create<TerminalState>()((set, get) => ({
  history: [],
  commandHistory: [],
  executing: false,

  executeCommand: async (cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return

    // ── /pinned — show / clear / remove pinned lines ──────────────
    const pinnedMatch = trimmed.match(/^\/pinned(?:\s+(.*))?$/i)
    if (pinnedMatch) {
      set(s => ({ commandHistory: [...s.commandHistory, trimmed] }))
      const sub = (pinnedMatch[1] ?? '').trim()
      const cmdLine: TerminalLine = {
        id: nextId(),
        type: 'command',
        content: trimmed,
        timestamp: now(),
      }
      let body: string
      if (sub === 'clear') {
        clearPinned()
        body = '  Pinned lines cleared.'
      } else if (sub.startsWith('rm ')) {
        const id = sub.slice(3).trim()
        const next = loadPinned().filter(e => e.id !== id)
        savePinned(next)
        body = `  Removed pinned id=${id}. ${next.length} remain.`
      } else {
        body = renderPinned(loadPinned())
      }
      const resultLine: TerminalLine = {
        id: nextId(),
        type: 'text',
        content: body,
        timestamp: now(),
      }
      set(s => ({ history: [...s.history, cmdLine, resultLine].slice(-500) }))
      return
    }

    // ── /alias — handled entirely client-side (localStorage) ───────
    const aliasCmd = trimmed.match(/^\/alias(?:\s+(.*))?$/i)
    if (aliasCmd) {
      set(s => ({ commandHistory: [...s.commandHistory, trimmed] }))
      const cmdLine: TerminalLine = {
        id: nextId(),
        type: 'command',
        content: trimmed,
        timestamp: now(),
      }
      const res = handleAliasCommand(aliasCmd[1] ?? '')
      const resultLine: TerminalLine = {
        id: nextId(),
        type: 'text',
        content: res.reply,
        timestamp: now(),
      }
      set(s => ({ history: [...s.history, cmdLine, resultLine].slice(-500) }))
      return
    }

    // ── Alias expansion — if `/foo` is defined in localStorage, run
    //    the stored command instead (including pipes). Prints what it
    //    expanded to so the user sees where their /foo resolved.
    const expanded = expandAlias(trimmed)
    if (expanded) {
      set(s => ({ commandHistory: [...s.commandHistory, trimmed] }))
      const hint: TerminalLine = {
        id: nextId(),
        type: 'system',
        content: `  alias ${trimmed} → ${expanded}`,
        timestamp: now(),
      }
      set(s => ({ history: [...s.history, hint].slice(-500) }))
      await get().executeCommand(expanded)
      return
    }

    // ── /focus TICKER macro ─────────────────────────────────────────
    // Runs the canonical "deep-dive" sequence for one ticker so you
    // don't have to type /q AAPL, /chart AAPL, /news AAPL, /earnings AAPL.
    const focusMatch = trimmed.match(/^\/focus\s+([A-Za-z0-9.\-]+)\s*$/i)
    if (focusMatch) {
      const sym = focusMatch[1].toUpperCase()
      set(s => ({ commandHistory: [...s.commandHistory, trimmed] }))
      const hint: TerminalLine = {
        id: nextId(),
        type: 'system',
        content: `  /focus ${sym} — running /quote, /chart, /news, /earnings in sequence`,
        timestamp: now(),
      }
      set(s => ({ history: [...s.history, hint].slice(-500) }))
      for (const c of [`/quote ${sym}`, `/chart ${sym} 6m`, `/news ${sym}`, `/earnings ${sym}`]) {
        await get().executeCommand(c)
      }
      return
    }

    // ── Pipe chain — run each `/cmd | /cmd` in sequence ────────────
    // Split on " | " boundaries so filenames / URLs with | don't break.
    if (/\s\|\s/.test(trimmed)) {
      const steps = trimmed.split(/\s+\|\s+/).map(s => s.trim()).filter(Boolean)
      if (steps.length > 1) {
        set(s => ({ commandHistory: [...s.commandHistory, trimmed] }))
        for (const c of steps) {
          await get().executeCommand(c)
        }
        return
      }
    }

    // Add command to command history (for up/down navigation)
    set(s => ({
      commandHistory: [...s.commandHistory, trimmed],
    }))

    // /clear, /cls, cls — all clear the terminal
    if (trimmed === '/clear' || trimmed === '/cls' || trimmed === 'cls') {
      set({ history: [] })
      return
    }

    // /grep PATTERN — client-side search over the terminal output history
    // No backend round-trip; writes a result summary directly to history.
    const grepMatch = trimmed.match(/^\/grep\s+(.+)$/i)
    if (grepMatch) {
      const pattern = grepMatch[1].trim()
      let re: RegExp
      try {
        re = new RegExp(pattern, 'i')
      } catch {
        re = new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i')
      }
      const grepCmdLine: TerminalLine = {
        id: nextId(),
        type: 'command',
        content: trimmed,
        timestamp: now(),
      }
      set(s => ({ history: [...s.history, grepCmdLine] }))
      const hits: string[] = []
      const current = get().history
      for (const line of current) {
        if (line.type === 'command') continue
        const clean = line.content.replace(/§[grabcdep]([^§]+)§/g, '$1')
        for (const row of clean.split('\n')) {
          if (re.test(row)) hits.push(`[${line.timestamp}] ${row.trim()}`)
        }
      }
      const capped = hits.slice(-100)
      const body =
        capped.length === 0
          ? `  /grep ${pattern}: no matches in ${current.length} lines`
          : `  /grep ${pattern}: ${hits.length} match${hits.length === 1 ? '' : 'es'}${hits.length > capped.length ? ` (showing last ${capped.length})` : ''}\n\n` +
            capped.join('\n')
      const grepResultLine: TerminalLine = {
        id: nextId(),
        type: 'text',
        content: body,
        timestamp: now(),
      }
      set(s => ({
        history: [...s.history, grepResultLine].slice(-500),
      }))
      return
    }

    // Add the user's command to the output
    const cmdLine: TerminalLine = {
      id: nextId(),
      type: 'command',
      content: trimmed,
      timestamp: now(),
    }
    set(s => ({ history: [...s.history, cmdLine], executing: true }))

    // Safety timeout: if the request hangs past 45s, force the input
    // back to usable so typing isn't locked forever. Happens if the
    // backend crashes or the WebSocket drops mid-command.
    const safetyTimeout = setTimeout(() => {
      set(s => {
        if (!s.executing) return s
        return {
          history: [
            ...s.history,
            {
              id: nextId(),
              type: 'error' as const,
              content: '  ERROR: command timed out after 45s (still running server-side)',
              timestamp: now(),
            },
          ].slice(-500),
          executing: false,
        }
      })
    }, 45_000)

    try {
      const { data } = await api.post<TerminalResponse>('/api/terminal/command', {
        command: trimmed,
      })

      const responseLine: TerminalLine = {
        id: nextId(),
        type: data.type as TerminalLine['type'],
        content: data.formatted,
        timestamp: now(),
        data: data,
      }
      set(s => ({
        history: [...s.history, responseLine].slice(-500),
        executing: false,
      }))
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Request failed')
          : 'Network error -- is the backend running?'

      const errorLine: TerminalLine = {
        id: nextId(),
        type: 'error',
        content: `  ERROR: ${message}`,
        timestamp: now(),
      }
      set(s => ({
        history: [...s.history, errorLine].slice(-500),
        executing: false,
      }))
    } finally {
      clearTimeout(safetyTimeout)
    }
  },

  clear: () => set({ history: [] }),

  addSystemMessage: (msg: string) => {
    const line: TerminalLine = {
      id: nextId(),
      type: 'system',
      content: msg,
      timestamp: now(),
    }
    set(s => ({
      history: [...s.history, line].slice(-500),
    }))
  },

  addLine: (line: Omit<TerminalLine, 'id'>) => {
    set(s => ({
      history: [...s.history, { ...line, id: nextId() }].slice(-500),
    }))
  },
}))

/** Convenience hook */
export function useTerminal() {
  return useTerminalStore()
}


// ── Multi-terminal state (for workspace panels) ───────────────────────────

export interface MultiTerminalInstance {
  history: TerminalLine[]
  commandHistory: string[]
  executing: boolean
}

interface MultiTerminalState {
  terminals: Record<string, MultiTerminalInstance>
  createTerminal: (id: string) => void
  removeTerminal: (id: string) => void
  executeCommand: (terminalId: string, cmd: string) => Promise<void>
  clearTerminal: (terminalId: string) => void
  addSystemMessage: (terminalId: string, msg: string) => void
  addLine: (terminalId: string, line: Omit<TerminalLine, 'id'>) => void
}

function _ensureTerminal(terminals: Record<string, MultiTerminalInstance>, id: string): Record<string, MultiTerminalInstance> {
  if (terminals[id]) return terminals
  return {
    ...terminals,
    [id]: { history: [], commandHistory: [], executing: false },
  }
}

export const useMultiTerminalStore = create<MultiTerminalState>()(
  persist(
  (set, get) => ({
  terminals: {},

  createTerminal: (id: string) => {
    set(s => ({
      terminals: _ensureTerminal(s.terminals, id),
    }))
  },

  removeTerminal: (id: string) => {
    set(s => {
      const { [id]: _, ...rest } = s.terminals
      return { terminals: rest }
    })
  },

  executeCommand: async (terminalId: string, cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return

    // Ensure terminal exists
    set(s => ({
      terminals: _ensureTerminal(s.terminals, terminalId),
    }))

    // Add to command history
    set(s => {
      const t = s.terminals[terminalId]
      if (!t) return s
      return {
        terminals: {
          ...s.terminals,
          [terminalId]: { ...t, commandHistory: [...t.commandHistory, trimmed] },
        },
      }
    })

    // /clear, /cls, cls — all clear the terminal
    if (trimmed === '/clear' || trimmed === '/cls' || trimmed === 'cls') {
      set(s => {
        const t = s.terminals[terminalId]
        if (!t) return s
        return {
          terminals: {
            ...s.terminals,
            [terminalId]: { ...t, history: [] },
          },
        }
      })
      return
    }

    // Add command line and set executing
    const cmdLine: TerminalLine = {
      id: nextId(),
      type: 'command',
      content: trimmed,
      timestamp: now(),
    }
    set(s => {
      const t = s.terminals[terminalId]
      if (!t) return s
      return {
        terminals: {
          ...s.terminals,
          [terminalId]: {
            ...t,
            history: [...t.history, cmdLine],
            executing: true,
          },
        },
      }
    })

    try {
      const { data } = await api.post<TerminalResponse>('/api/terminal/command', {
        command: trimmed,
      })

      const responseLine: TerminalLine = {
        id: nextId(),
        type: data.type as TerminalLine['type'],
        content: data.formatted,
        timestamp: now(),
        data: data,
      }
      set(s => {
        const t = s.terminals[terminalId]
        if (!t) return s
        return {
          terminals: {
            ...s.terminals,
            [terminalId]: {
              ...t,
              history: [...t.history, responseLine].slice(-300),
              executing: false,
            },
          },
        }
      })
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: { detail?: string } } }).response?.data?.detail ?? 'Request failed')
          : 'Network error -- is the backend running?'

      const errorLine: TerminalLine = {
        id: nextId(),
        type: 'error',
        content: `  ERROR: ${message}`,
        timestamp: now(),
      }
      set(s => {
        const t = s.terminals[terminalId]
        if (!t) return s
        return {
          terminals: {
            ...s.terminals,
            [terminalId]: {
              ...t,
              history: [...t.history, errorLine].slice(-300),
              executing: false,
            },
          },
        }
      })
    }
  },

  clearTerminal: (terminalId: string) => {
    set(s => {
      const t = s.terminals[terminalId]
      if (!t) return s
      return {
        terminals: {
          ...s.terminals,
          [terminalId]: { ...t, history: [] },
        },
      }
    })
  },

  addSystemMessage: (terminalId: string, msg: string) => {
    const line: TerminalLine = {
      id: nextId(),
      type: 'system',
      content: msg,
      timestamp: now(),
    }
    set(s => {
      const t = s.terminals[terminalId] ?? { history: [], commandHistory: [], executing: false }
      return {
        terminals: {
          ...s.terminals,
          [terminalId]: {
            ...t,
            history: [...t.history, line].slice(-300),
          },
        },
      }
    })
  },

  addLine: (terminalId: string, line: Omit<TerminalLine, 'id'>) => {
    set(s => {
      const t = s.terminals[terminalId] ?? { history: [], commandHistory: [], executing: false }
      return {
        terminals: {
          ...s.terminals,
          [terminalId]: {
            ...t,
            history: [...t.history, { ...line, id: nextId() }].slice(-300),
          },
        },
      }
    })
  },
}),
  {
    name: 'av-terminal-history',
    partialize: (state) => ({
      // Only persist command history per terminal, not output or executing state
      terminals: Object.fromEntries(
        Object.entries(state.terminals).map(([id, t]) => [
          id,
          { history: [], commandHistory: t.commandHistory.slice(-50), executing: false },
        ])
      ),
    }),
  },
))
